import React, {
  useContext, FC, useEffect, useRef, useState,
} from 'react';
import LinearProgress from '@fv-components/linear-progress';

import useQueryProjects from '../../util-api/useQueryProjects';
import useQueryRecentProjects from '../../util-api/useQueryRecentProjects';
import { getIsDesktop, getIsOutlook } from '../../util-helpers/office';
import useDebounce from '../../util-hooks/useDebounce';
import useInput from '../../util-hooks/useInput';
import {
  getCurrentOrg,
  getUserId,
  getUserOrgsAsOptions,
  setOrgId,
} from '../../fvid-auth/userSession';
import FilevineIcon from '../FilevineIcon';
import InlineOrgPicker from '../InlineOrgPicker';
import ProjectContext from '../ProjectContext';

const css = require('./ProjectSearchBar.module.scss');

interface IProjectSearchBarProps {
  clearCurrentProject: VoidFunction;
  onProjectSearchTerm: (term?: string) => void;
}

const ProjectSearchBar: FC<IProjectSearchBarProps> = ({
  clearCurrentProject,
  onProjectSearchTerm,
}) => {
  const userId = getUserId();
  const currentOrg = getCurrentOrg();
  const userOrgs = getUserOrgsAsOptions();
  const [values, handleValueChange] = useInput({ projectSearchTerm: '' });
  const debouncedSearchTerm = useDebounce(values.projectSearchTerm, 300);
  const projectSearchQuery = useQueryProjects(debouncedSearchTerm || null);
  const recentProjectsQuery = useQueryRecentProjects(userId, currentOrg?.orgId || null);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeQuery = !debouncedSearchTerm ? recentProjectsQuery : projectSearchQuery;

  const {
    currentProjectData,
    setCurrentProject,
    setProjectSearchResults,
  } = useContext(ProjectContext);
  const isDesktop = getIsDesktop();

  // Track org changes to clear search
  const [, setSelectedOrgId] = useState<number>();
  const handleOrgIdSelected = (id: number) => {
    // Clear search on org change
    handleValueChange({
      target: {
        name: 'projectSearchTerm',
        value: '',
      },
    });

    setSelectedOrgId(id);
    setOrgId(id);

    // If the user is in a project view and changes orgs we need to go back to the search
    if (currentProjectData) {
      setCurrentProject(undefined);
    }
  };

  useEffect(() => {
    // Convert TanStack query to the format expected by context
    const results = {
      called: activeQuery.isFetched,
      loading: activeQuery.isLoading,
      error: activeQuery.error as any,
      count: activeQuery.data?.count || 0,
      items: activeQuery.data?.items || [],
      hasMore: activeQuery.data?.hasMore || false,
      limit: activeQuery.data?.limit || 0,
    };
    setProjectSearchResults(results);
  }, [activeQuery.data, activeQuery.isLoading, activeQuery.isFetched, activeQuery.error, setProjectSearchResults]);


  useEffect(() => {
    if (!currentProjectData) {
      onProjectSearchTerm(debouncedSearchTerm);
    }
  }, [onProjectSearchTerm, debouncedSearchTerm, currentProjectData]);

  return (
    <div className={css.searchHeader}>
      {userOrgs && userOrgs.length > 1 && (
        <InlineOrgPicker onOrgSelected={handleOrgIdSelected} />
      )}

      {(getIsOutlook() || getCurrentOrg()?.pdfEditEnabled)
        && (
          <>
            <div className={css.searchContainer}>
              {isDesktop
                ? (
                  <span className={css.searchBarLogoContainer}>
                    <img className={css.searchBarLogo} src="assets/logo_32.png" alt="Filevine" />
                  </span>
                )
                : <span />}
              {!currentProjectData
                ? (
                  <input
                    ref={inputRef}
                    className={css.searchBar}
                    data-test="project-search"
                    name="projectSearchTerm"
                    placeholder="Search for a project by name or client."
                    value={values.projectSearchTerm}
                    onChange={handleValueChange}
                  />
                )
                : (
                  <div className={css.searchTitle}>
                    <a href={currentProjectData.projectUrl} target="_blank" rel="noopener noreferrer">
                      <span className={css.projectName}>{currentProjectData.projectOrClientName}</span>
                    </a>
                    <button
                      aria-label="search"
                      className={css.searchBtn}
                      type="button"
                      onClick={clearCurrentProject}
                      onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
                        if (e.key === 'Enter') {
                          clearCurrentProject();
                        }
                      }}
                    >
                      <FilevineIcon className={css.searchIcon} icon="search" />
                    </button>
                  </div>
                )}
            </div>
            {!currentProjectData && (
              <LinearProgress
                indeterminate={activeQuery.isLoading}
                closed={!activeQuery.isLoading}
              />
            )}
          </>
        )}
    </div>
  );
};

export default ProjectSearchBar;